export type SystemTemplateModuleDto = {
  moduleId: string;
  moduleName: string | null;
  totalCount: number;
  enabledCount: number;
  disabledCount: number;
  isAllowedToEdit: boolean;
};

export type CompanyTemplateModuleDto = {
  moduleId: string;
  moduleName: string | null;
  totalCount: number;
  enabledCount: number;
  disabledCount: number;
  isAllowedToEdit: boolean;
};

export type TenantTemplateModuleDto = SystemTemplateModuleDto & {
  companiesCount: number;
};

export type CompaniesProductsCountDto = Record<string, number>;

export enum TemplateScope {
  Default = 'Default',
  Tenant = 'Tenant',
  Company = 'Company',
}

/**
 * The page's Notifications Level (route-resolved). Not the same as `TemplateScope`: an Effective
 * Template on the Tenant/Company page can still have `scope = Default` when not yet overridden.
 */
export enum NotificationsLevel {
  Default = 'Default',
  Tenant = 'Tenant',
  Company = 'Company',
}

export type TemplateListItemDto = {
  id: string;
  name: string | null;
  version: string | null;
  moduleId: string;
  moduleName: string | null;
  scope: TemplateScope;
  isSystem: boolean;
  createdDate: string;
  updatedDate: string | null;
  isEnabled: boolean;
  isAllowedToEdit: boolean;
};

export type TemplateListResponseDto = {
  items: TemplateListItemDto[] | null;
  totalCount: number;
};

export type TemplateWriteRequest = {
  templateId?: string;
  parentId?: string;
  tenantId: string;
  companyId?: string;
};

export enum RecipientCategory {
  To = 'To',
  Cc = 'Cc',
  Bcc = 'Bcc',
}

// SectionContactEmail option has been removed according to https://dev.azure.com/ITMCloud/ITM-Data/_workitems/edit/159115/
export enum RecipientType {
  UserRoleManager = 'UserRoleManager',
  UserRoleManagerWithSftp = 'UserRoleManagerWithSftp',
  UserRoleCustomerOperations = 'UserRoleCustomerOperations',
  UserRoleCustomerOperationsWithSftp = 'UserRoleCustomerOperationsWithSftp',
  UserRoleClientAdmin = 'UserRoleClientAdmin',
  UserRoleClientAdminWithSftp = 'UserRoleClientAdminWithSftp',
  UserRoleSupport = 'UserRoleSupport',
  UserRoleAnalyst = 'UserRoleAnalyst',
  CompanyContactEmail = 'CompanyContactEmail',
  SchemeContactEmail = 'SchemeContactEmail',
  EmployerEmail = 'EmployerEmail',
  Custom = 'Custom',
  ActionUser = 'ActionUser',
  Member = 'Member',
}

export type CustomRecipientInfoRequest = {
  emailAddress?: string;
  recipientFullName?: string;
};

export type CustomRecipientInfoDto = {
  emailAddress: string | null;
  recipientFullName: string | null;
};

export type RecipientRequest = {
  type?: RecipientType;
  category?: RecipientCategory;
  customRecipientInfo?: CustomRecipientInfoRequest;
};

export type RecipientDto = {
  type: RecipientType;
  category: RecipientCategory;
  customRecipientInfo: CustomRecipientInfoDto;
};

export type TemplateRecipientsWriteRequest = TemplateWriteRequest & {
  recipients: RecipientRequest[];
};

export type SystemTemplateRecipientsWriteRequest = {
  templateId: string;
  recipients: RecipientRequest[];
};

export type TemplateContentWriteRequest = TemplateWriteRequest & {
  subject: string;
  /** Base64 of the UTF-8 MJML (`encodeUtf8ToBase64`): AWS WAF blocks MJML markup in a request body */
  markdownContent: string;
};

export type TemplateTestEmailRequest = {
  templateId: string;
  tenantId?: string;
  companyId?: string;
  subject: string;
  /** Base64 of the UTF-8 MJML (`encodeUtf8ToBase64`): AWS WAF blocks MJML markup in a request body */
  markdownContent: string;
  email: string;
};

export type SystemTemplateContentWriteRequest = {
  templateId: string;
  subject: string;
  /** Base64 of the UTF-8 MJML (`encodeUtf8ToBase64`): AWS WAF blocks MJML markup in a request body */
  markdownContent: string;
};

export type TemplateDetailsDto = TemplateListItemDto & {
  companyId: string | null;
  tenantId: string | null;
  subject: string | null;
  markdownContent: string | null;
  recipients: RecipientDto[] | null;
  dynamicFields: string[] | null;
  isRecipientsToEditable: boolean;
  isRecipientsEditable: boolean;
  availableRecipientsTo: RecipientType[] | null;
  availableRecipientsCCandBCC: RecipientType[] | null;
};

export enum TemplateAuditChangeType {
  RecipientChange = 'RecipientChange',
  StatusChange = 'StatusChange',
  ContentChange = 'ContentChange',
}

export type TemplateAuditListItemDto = {
  id: string;
  templateId: string;
  userId: string;
  userName: string | null;
  changedAt: string;
  productId: string;
  productName: string | null;
  templateName: string | null;
  changeType: TemplateAuditChangeType;
  templateScope: TemplateScope;
  tenantId: string | null;
  tenantName: string | null;
  companyId: string | null;
  companyName: string | null;
  canViewDetails: boolean;
};

export type TemplateAuditListItemDtoPagedResponse = {
  totalCount: number;
  items: TemplateAuditListItemDto[] | null;
};

export type TemplateAuditBasicInformationDto = Omit<TemplateAuditListItemDto, 'canViewDetails'>;

export type TemplateAuditStatusValue = {
  isEnabled: boolean;
};

// audit values are serialized without null fields, so they are omitted rather than null
export type TemplateAuditRecipient = Omit<RecipientDto, 'customRecipientInfo'> & {
  customRecipientInfo?: {
    emailAddress?: string;
    recipientFullName?: string;
  };
};

export type TemplateAuditRecipientsValue = {
  recipients: TemplateAuditRecipient[];
};

export type TemplateAuditComparisonValue = TemplateAuditStatusValue | TemplateAuditRecipientsValue;

/**
 * Swagger describes the values as untyped JSON; their shape is synchronized with BE `TemplateAuditRecorder`
 */
export type TemplateAuditComparisonDto = {
  previousValue: TemplateAuditComparisonValue;
  newValue: TemplateAuditComparisonValue;
};

export type TemplateAuditDetailsDto = {
  basicInformation: TemplateAuditBasicInformationDto;
  comparison: TemplateAuditComparisonDto;
};

export enum EmailStatus {
  Queued = 'Queued',
  Bounce = 'Bounce',
  Complaint = 'Complaint',
  Delivery = 'Delivery',
  Send = 'Send',
  Reject = 'Reject',
  Open = 'Open',
  Click = 'Click',
  RenderingFailure = 'RenderingFailure',
  DeliveryDelay = 'DeliveryDelay',
  Subscription = 'Subscription',
  InvalidEmail = 'InvalidEmail',
  HasNoDnsRecords = 'HasNoDnsRecords',
}

export enum EmailAuditType {
  To = 'To',
  CC = 'CC',
  BCC = 'BCC',
}

export enum BounceType {
  Undetermined = 'Undetermined',
  Permanent = 'Permanent',
  Transient = 'Transient',
}

export enum ComplaintType {
  Abuse = 'Abuse',
  AuthFailure = 'AuthFailure',
  Fraud = 'Fraud',
  NotSpam = 'NotSpam',
  Other = 'Other',
  Virus = 'Virus',
}

export enum Reason {
  Undetermined = 'Undetermined',
  General = 'General',
  NoEmail = 'NoEmail',
  Suppressed = 'Suppressed',
  OnAccountSuppressionList = 'OnAccountSuppressionList',
  EmailValidationSuppressed = 'EmailValidationSuppressed',
  OnTenantSuppressionList = 'OnTenantSuppressionList',
  MailboxFull = 'MailboxFull',
  MessageTooLarge = 'MessageTooLarge',
  CustomTimeoutExceeded = 'CustomTimeoutExceeded',
  ContentRejected = 'ContentRejected',
  AttachmentRejected = 'AttachmentRejected',
}

export type EmailLogsTilesDto = {
  totalDelivered: number;
  totalFailed: number;
  totalPending: number;
};

export type EmailLogItemDto = {
  sentDateTime: string;
  productId: string | null;
  productName: string | null;
  notification: string | null;
  recipient: string | null;
  recipientType: EmailAuditType;
  status: EmailStatus;
  bounceType: BounceType | null;
  complaintType: ComplaintType | null;
  reason: Reason | null;
};

export type EmailLogItemDtoPagedResponse = {
  totalCount: number;
  items: EmailLogItemDto[] | null;
};

export type AccountEmailLogItemDto = {
  sentDateTime: string;
  productId: string | null;
  productName: string | null;
  notification: string | null;
  recipient: string | null;
  recipientType: EmailAuditType;
  status: EmailStatus;
  bounceType: BounceType | null;
  complaintType: ComplaintType | null;
  reason: Reason | null;
};

export type AccountEmailLogItemDtoPagedResponse = {
  totalCount: number;
  items: AccountEmailLogItemDto[] | null;
};
